import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MatRadioModule } from '@angular/material/radio';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ShippingAddress } from '@api/model/shippingAddress';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, of } from 'rxjs';
import { delay, take } from 'rxjs/operators';
import { LetDirective } from '@ngrx/component';
import { StickyButtonDirective } from '@core/directives/btn-sticky.directive';
import { LoadingOverlayComponent } from '@core/components/loading-overlay/loading-overlay.component';
import { AddressListComponent } from '@core/components/address-list/address-list.component';
import { PbbButtonComponent } from '@core/components/pbb-button/pbb-button.component';
import { PbbSvgIconComponent } from '@core/components';
import { Store } from '@ngrx/store';
import { selectUserProfileAddresses, selectUserProfileDefaultShippingAddressRef } from '@store/user/user.selectors';
import { UntilDestroy } from '@core/classes/untilDestroy.class';
import {
  selectMerchantAddresses,
  selectMerchantName,
  selectIsSelectingShippingAddress,
  selectSelectedShippingAddress,
  selectIsLoadingShippingOptions,
} from '@store/checkout/checkout.selectors';
import * as CheckoutActions from '@store/checkout/checkout.actions';
import { createAddressValidationForm, validateObjectWithForm } from '@core/validators/validators';

@Component({
  selector: 'pbb-select-shipping-address-page',
  imports: [
    CommonModule,
    MatRadioModule,
    PbbButtonComponent,
    TranslateModule,
    StickyButtonDirective,
    LetDirective,
    LoadingOverlayComponent,
    AddressListComponent,
    PbbSvgIconComponent,
    RouterLink,
  ],
  templateUrl: './select-shipping-address-page.component.html',
  styleUrls: ['./select-shipping-address-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectShippingAddressPageComponent extends UntilDestroy {
  // Merchant-provided shipping addresses shown at the top of the page.
  merchantAddresses$ = this.store.select(selectMerchantAddresses);
  // Saved Konek profile addresses available for selection.
  userAddresses$ = this.store.select(selectUserProfileAddresses);
  // Default Konek shipping address ref for the default badge.
  userDefaultAddressRef$ = this.store.select(selectUserProfileDefaultShippingAddressRef);
  // Merchant display name used in the merchant address section header.
  merchantName$ = this.store.select(selectMerchantName);
  // Loading flag while shipping address selection/options are being processed.
  isLoadingShippingAddress$ = this.store.select(selectIsLoadingShippingOptions);
  // Currently selected shipping address resolved from merchant or Konek lists.
  selectedShippingAddress$ = this.store.select(selectSelectedShippingAddress);

  addressValidationForm: FormGroup;

  constructor(private readonly router: Router, private readonly store: Store, private readonly fb: FormBuilder) {
    super();
    this.addressValidationForm = createAddressValidationForm(this.fb);
  }

  // Handler to select a shipping address
  onAddressSelect(address: ShippingAddress): void {
    // Only dispatch if address has both ref and postal code
    if (!address?.shipping_address_ref || !address.postal_code) {
      return;
    }
    this.store.dispatch(
      CheckoutActions.selectShippingAddressWithPostalCode({
        addressRef: address.shipping_address_ref,
        postalCode: address.postal_code,
      })
    );
  }

  onContinueClick(): void {
    combineLatest([this.selectedShippingAddress$, this.merchantAddresses$])
      .pipe(take(1))
      .subscribe(([selectedAddress, merchantAddresses]) => {
        if (!selectedAddress?.shipping_address_ref || !selectedAddress?.postal_code) {
          return;
        }

        // Check if selected address is a merchant address
        const isMerchantAddress = merchantAddresses?.some(
          (addr) => addr.shipping_address_ref === selectedAddress.shipping_address_ref
        );
 
        // If merchant address, go to save page
        if (isMerchantAddress) {
          this.router.navigate(['/checkout/address/edit-merchant'], {
            state: {
              selectedAddress: selectedAddress,
              isEdit: false,
              continueToShipping: true,
            },
          })
          return;
        }

        // For Konek addresses, validate against form structure
        const isValid = validateObjectWithForm(selectedAddress, this.addressValidationForm);

        if (!isValid) {
          // Invalid - send to save form
          this.router.navigate(['/checkout/address/edit-merchant'], {
            state: {
              selectedAddress: selectedAddress,
              isEdit: true,
              continueToShipping: true,
            },
          });
          return;
        }
        // Valid Konek address -> dispatch action
        this.store.dispatch(
          CheckoutActions.selectShippingAddressAndFetchOptions({
            shippingAddress: selectedAddress,
          })
        );
      });
  }

  onEditAddress(event: { address: any; index: number }): void {
    this.router.navigate(['/checkout/address/edit', event.index]);
  }

  getMerchantDefaultRef(addresses: any[]): string {
    return addresses?.find((addr) => addr.default_shipping_address)?.shipping_address_ref || '';
  }
}
